import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from "@angular/core";
import {NgxFlickingComponent, Plugin} from "@egjs/ngx-flicking";
import {Arrow} from "@egjs/flicking-plugins";
import {Compbaser} from "../../../components/compbaser";
import {HomeInspireService, IInspireVideo} from "./home-inspire.service";

/** "Get inspired" — a horizontally scrollable strip of short industry
 *  intro clips served by the Express /_videos route.
 *
 *  PERFORMANCE:
 *   - The strip uses a CDK horizontal virtual-scroll viewport, so only the
 *     handful of cards on screen are in the DOM.
 *   - Every tile (strip + dialog filmstrip) is the SAME <app-inspire-thumb>
 *     backed by HomeInspireService, which captures each clip's first frame
 *     ONCE and caches it as an <img> — no re-fetch / re-decode when the
 *     dialog opens. A gentle idle prefetch warms that cache so the dialog
 *     filmstrip is instant.
 *   - The full mp4 is only loaded inside the lightbox, on click.
 *
 *  Lightbox playback: always muted (forceMute works around Angular #15592)
 *  and capped at the first 6s (timeupdate snaps back to 0; loop continues). */
@Component({
    selector: "app-home-inspire",
    templateUrl: "./home-inspire.component.html",
    styleUrls: ["./home-inspire.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HomeInspireComponent extends Compbaser implements OnInit {
    /** Seconds of each clip we ever show. Past this we snap back to 0. */
    static readonly CLIP_SECONDS = 6;

    /** Single source of truth — shared with the dialog filmstrip + thumbs. */
    readonly videos: IInspireVideo[] = this.inspireService.videos;

    /** ngx-flicking plugins for the dialog filmstrip — Arrow wires the
     *  prev/next nav buttons (same carousel lib the templates page uses). */
    readonly stripPlugins: Plugin[] = [new Arrow({moveCount: 3})];

    /** The main strip carousel — driven by the header prev/next arrows. */
    @ViewChild("stripFlick") private stripFlick?: NgxFlickingComponent;

    /** The clip currently expanded in the lightbox, or null when closed. */
    lightbox: IInspireVideo | null = null;
    /** True while the selected lightbox clip is still buffering — drives
     *  the loader overlay. Set when a clip is picked, cleared once the
     *  <video> can actually play (or errors/stalls). */
    lightboxLoading = false;

    constructor(
        private inspireService: HomeInspireService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        // Warm the first-frame cache in the background so the strip + dialog
        // fill in fast. No-op after the first call.
        this.inspireService.prefetchThumbs();
    }

    /** Header arrow → step the strip carousel. prev()/next() reject at the
     *  ends (bound:true), so swallow that to avoid console noise. */
    stripPrev(): void {
        this.stripFlick?.prev().catch(() => {});
    }

    stripNext(): void {
        this.stripFlick?.next().catch(() => {});
    }

    /** Keep the clip inside the first CLIP_SECONDS — snap back to 0 once
     *  we cross the cap so we only ever preview the opening. */
    onTimeUpdate(ev: Event): void {
        const video = ev.target as HTMLVideoElement;
        if (video.currentTime >= HomeInspireComponent.CLIP_SECONDS) {
            video.currentTime = 0;
        }
    }

    /** Force-mute a <video> from JS. Angular doesn't reliably reflect the
     *  static `muted` attribute to the element property (angular/angular
     *  #15592), so an autoplaying clip can come through with sound. Setting
     *  the property + volume on the element guarantees silence. */
    forceMute(ev: Event): void {
        const video = ev.target as HTMLVideoElement;
        video.muted = true;
        video.volume = 0;
    }

    openLightbox(video: IInspireVideo): void {
        this.lightbox = video;
        this.lightboxLoading = true;
        this.cdr.markForCheck();
    }

    /** Switch the open lightbox to another clip from the filmstrip. The
     *  player reuses the same <video>; changing [src] reloads + autoplays
     *  it (still muted via forceMute). */
    selectLightbox(video: IInspireVideo): void {
        if (this.lightbox?.file === video.file) return;
        this.lightbox = video;
        this.lightboxLoading = true;
        this.cdr.markForCheck();
    }

    /** The lightbox <video> can play (or has errored/stalled) — drop the
     *  loader overlay so it never spins forever on a failed clip. */
    onLightboxReady(): void {
        this.lightboxLoading = false;
        this.cdr.markForCheck();
    }

    /** The lightbox <video> is (re)buffering — show the loader again. */
    onLightboxBuffering(): void {
        this.lightboxLoading = true;
        this.cdr.markForCheck();
    }

    closeLightbox(): void {
        this.lightbox = null;
        this.cdr.markForCheck();
    }
}
